import React, { useEffect, useState } from 'react';
import { Copy, Check, Code2 } from 'lucide-react';
import { restFetch, RCard } from './restui';

/* KITXENS Pixel v2 — client-facing connection card. Shows Connected/Offline,
   last seen, version, events and health; owners get the install snippet. */

const ago = (iso, isEs) => {
  if (!iso) return isEs ? 'nunca' : 'never';
  const s = Math.max(1, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) return `${Math.round(s / 3600)}h`;
  return `${Math.round(s / 86400)}d`;
};

function Stat({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[9.5px] uppercase tracking-[0.15em] font-bold text-gray-400">{label}</p>
      <p className="mt-0.5 text-[14px] font-bold text-gray-900 tabular-nums truncate">{value}</p>
    </div>
  );
}

export default function PixelStatusCard({ isEs, canInstall }) {
  const [d, setD] = useState(null);
  const [snippet, setSnippet] = useState(null);
  const [copied, setCopied] = useState(false);
  const load = () => restFetch('/pixel/status').then((r) => (r.ok ? r.json() : null)).then(setD).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  if (!d) return null;
  const showInstall = async () => {
    if (snippet) { setSnippet(null); return; }
    const r = await restFetch('/pixel/provision', { method: 'POST' });
    if (r.ok) { const j = await r.json(); setSnippet(j); load(); }
  };
  const copy = () => {
    navigator.clipboard?.writeText(snippet.snippet).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  const on = d.connected;
  return (
    <RCard className="p-4" data-testid="pixel-status-card">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <span className="inline-flex items-center gap-2 font-bold text-[13.5px] text-gray-900" data-testid="pixel-connected-state">
          <span className={`w-2.5 h-2.5 rounded-full ${on ? 'bg-emerald-500 animate-pulse' : 'bg-gray-300'}`} />
          KITXENS Pixel {on ? (isEs ? '· Conectado' : '· Connected') : (d.last_seen ? (isEs ? '· Sin señal' : '· Offline') : (isEs ? '· Sin instalar' : '· Not installed'))}
          {d.demo && <span className="text-[9.5px] uppercase tracking-wider font-bold text-gray-300">demo</span>}
        </span>
        <div className="ml-auto flex items-center gap-3">
          {canInstall && (
            <button onClick={showInstall} data-testid="pixel-install-btn"
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 hover:border-[var(--rest-accent)] text-gray-700 font-bold text-[11.5px] px-3 py-1.5 transition-colors">
              <Code2 className="w-3.5 h-3.5" /> {d.provisioned ? (isEs ? 'Ver instalación' : 'View install') : (isEs ? 'Instalar Pixel' : 'Install Pixel')}
            </button>
          )}
        </div>
      </div>
      <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label={isEs ? 'Última señal' : 'Last seen'} value={d.last_seen ? `${ago(d.last_seen, isEs)} ago` : '—'} testid="pixel-last-seen" />
        <Stat label={isEs ? 'Sitio web' : 'Website'} value={d.website || '—'} testid="pixel-website" />
        <Stat label={isEs ? 'Versión' : 'Version'} value={d.version || '—'} testid="pixel-version" />
        <Stat label={isEs ? 'Entorno' : 'Environment'} value={d.environment || '—'} testid="pixel-environment" />
        <Stat label={isEs ? 'Eventos hoy · 7d' : 'Events today · 7d'} value={`${(d.events_today ?? 0).toLocaleString()} · ${(d.events_7d ?? 0).toLocaleString()}`} testid="pixel-events" />
        <Stat label="Health" value={d.health != null ? `${d.health}%` : '—'} testid="pixel-health" />
      </div>
      {snippet && (
        <div className="mt-3 rounded-xl bg-[#141518] p-3.5" data-testid="pixel-snippet">
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#F77F11]">
              Pixel ID {snippet.pixel_id} — {isEs ? 'pega esto antes de </body> en tu web' : 'paste this before </body> on your website'}
            </p>
            <button onClick={copy} data-testid="pixel-snippet-copy"
              className="inline-flex items-center gap-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-[10.5px] font-bold px-2.5 py-1 transition-colors">
              {copied ? <Check className="w-3 h-3 text-emerald-400" /> : <Copy className="w-3 h-3" />} {copied ? (isEs ? 'Copiado' : 'Copied') : (isEs ? 'Copiar' : 'Copy')}
            </button>
          </div>
          <pre className="mt-2 text-[11px] text-emerald-300 whitespace-pre-wrap break-all font-mono">{snippet.snippet}</pre>
          <p className="mt-2 text-[10.5px] text-white/50">
            {isEs ? 'Verifícalo en la consola del navegador: window.__KITXENS_PIXEL__' : 'Verify it in the browser console: window.__KITXENS_PIXEL__'}
          </p>
        </div>
      )}
    </RCard>
  );
}
